/**
 * Cart hooks — Task 35.1, 35.2
 */
import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, getErrorMessage } from '@/lib/api'
import { useCartStore } from '@/stores/cartStore'
import { useToast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import type { Cart } from '@/types'

/**
 * Generate a unique session token for guest carts
 */
function generateSessionToken(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export function useCartHeaders() {
  const sessionToken = useCartStore((s) => s.sessionToken)
  const { isAuthenticated } = useAuthStore()

  // Initialize session token for guests if not already set
  useEffect(() => {
    if (!isAuthenticated && !sessionToken) {
      const newToken = generateSessionToken()
      useCartStore.getState().setSessionToken(newToken)
    }
  }, [isAuthenticated, sessionToken])

  return useMemo(
    () => (sessionToken ? { 'X-Session-Token': sessionToken } : {}),
    [sessionToken],
  )
}

// ─── Fetch cart ───────────────────────────────────────────────────────────────

export function useCart() {
  const { setCart, setSessionToken } = useCartStore()
  const headers = useCartHeaders()

  return useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const { data } = await api.get<{
        cart: { id: number; session_token: string | null; coupon_id: number | null; expires_at: string | null } | null
        items: Cart['items']
        coupon: { code?: string } | null
        coupon_code: string | null
        subtotal: number
        promotion_discount: number
        coupon_discount: number
        loyalty_discount: number
        loyalty_points_redeemed?: number
        loyalty_max_points?: number
        discount_amount: number
        tax_amount: number
        cgst_amount: number
        sgst_amount: number
        grand_total: number
        applied_promotions: Cart['applied_promotions']
        price_changed: boolean
      }>('/cart', { headers })

      if (!data.cart) {
        setCart(null)
        return null
      }

      // Map backend response to our Cart type
      const items = (data.items ?? []).map((item: any) => ({
        ...item,
        unit_price: item.current_price ?? item.unit_price_snapshot ?? 0,
        subtotal: (item.current_price ?? item.unit_price_snapshot ?? 0) * (item.quantity ?? 1),
      }))

      const cart: Cart = {
        id: data.cart.id,
        session_token: data.cart.session_token,
        items,
        subtotal: data.subtotal ?? 0,
        discount_amount: data.discount_amount ?? 0,
        coupon_code: data.coupon_code ?? data.coupon?.code ?? null,
        coupon_discount: data.coupon_discount ?? 0,
        loyalty_discount: data.loyalty_discount ?? 0,
        loyalty_points_redeemed: data.loyalty_points_redeemed ?? 0,
        loyalty_max_points: data.loyalty_max_points ?? 0,
        tax_amount: data.tax_amount ?? 0,
        cgst_amount: data.cgst_amount ?? 0,
        sgst_amount: data.sgst_amount ?? 0,
        shipping_amount: 0,
        grand_total: data.grand_total ?? data.subtotal ?? 0,
        applied_promotions: data.applied_promotions ?? [],
      }

      if (data.cart.session_token) {
        setSessionToken(data.cart.session_token)
      }
      setCart(cart)
      return cart
    },
    staleTime: 0,
  })
}

// ─── Add to cart ──────────────────────────────────────────────────────────────

export function useAddToCart() {
  const qc = useQueryClient()
  const headers = useCartHeaders()
  const { setSessionToken } = useCartStore()
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async (payload: {
      product_id: number
      variant_id?: number
      quantity: number
      product_name?: string
    }) => {
      // Clean up payload - only include variant_id if it exists
      const cleanPayload: any = {
        product_id: payload.product_id,
        quantity: payload.quantity,
      }
      if (payload.variant_id) {
        cleanPayload.variant_id = payload.variant_id
      }

      const { data } = await api.post<{ item: unknown; session_token: string | null }>(
        '/cart/items',
        cleanPayload,
        { headers },
      )
      // Store session token for guest carts
      if (data.session_token) {
        setSessionToken(data.session_token)
      }
      return { data, productName: payload.product_name }
    },
    onSuccess: (response) => {
      const message = response?.productName 
        ? `Added "${response.productName}" to cart`
        : 'Added to cart'
      success(message)
      // Invalidate and refetch cart immediately
      qc.invalidateQueries({ queryKey: ['cart'] })
      // Force a refetch after invalidation
      qc.refetchQueries({ queryKey: ['cart'] })
    },
    onError: (err) => {
      toastError('Could not add to cart', getErrorMessage(err))
    },
  })
}

// ─── Update quantity ──────────────────────────────────────────────────────────

export function useUpdateCartItem() {
  const qc = useQueryClient()
  const headers = useCartHeaders()

  return useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: number; quantity: number }) => {
      const { data } = await api.put<{ item: unknown }>(`/cart/items/${itemId}`, { quantity }, { headers })
      return data.item
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  })
}

// ─── Remove item ──────────────────────────────────────────────────────────────

export function useRemoveCartItem() {
  const qc = useQueryClient()
  const headers = useCartHeaders()

  return useMutation({
    mutationFn: async (itemId: number) => {
      await api.delete(`/cart/items/${itemId}`, { headers })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  })
}

// ─── Apply coupon ─────────────────────────────────────────────────────────────

export function useApplyCoupon() {
  const qc = useQueryClient()
  const headers = useCartHeaders()
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<{ data: Cart }>('/cart/coupon', { code }, { headers })
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] })
      success('Coupon applied!')
    },
    onError: (err) => {
      toastError('Invalid coupon', getErrorMessage(err))
    },
  })
}

export function useRemoveCoupon() {
  const qc = useQueryClient()
  const headers = useCartHeaders()

  return useMutation({
    mutationFn: async () => {
      await api.delete('/cart/coupon', { headers })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  })
}

// ─── Redeem loyalty points ────────────────────────────────────────────────────

export function useRedeemLoyaltyPoints() {
  const qc = useQueryClient()
  const headers = useCartHeaders()
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async (points: number) => {
      await api.post('/cart/loyalty', { points }, { headers })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      success('Loyalty points applied!')
    },
    onError: (err) => {
      toastError('Could not redeem points', getErrorMessage(err))
    },
  })
}

export function useRemoveLoyaltyPoints() {
  const qc = useQueryClient()
  const headers = useCartHeaders()
  const { success, error: toastError } = useToast()

  return useMutation({
    mutationFn: async () => {
      await api.delete('/cart/loyalty', { headers })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] })
      success('Loyalty points removed')
    },
    onError: (err) => {
      toastError('Could not remove points', getErrorMessage(err))
    },
  })
}
